<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import PageList from 'hydria/components/PageList.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/notifications' : `/notifications?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/notifications' : `/notifications?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    // let searchQueryString: string
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (sOptions.q != '') sdata.text = sOptions.q
        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        // searchQueryString = encodeURIComponent(JSON.stringify(sdata))

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/general/1/notifications', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        // const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            if (item['stamp']) item['stamp'] = toLuxon(item['stamp'])
            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })
</script>

<Breadcrumb crumbs={[{ txt: tgettext('Notifications') }]} />

<div class="card">
    <div class="card-title text-primary">{$_('Search parameters')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
            class="form"
        >
            <div class="row">
                <div class="col-sm-9">
                    <div class="input-group">
                        <div class="input-group-text">{$_('Text')}</div>
                        <input type="text" class="form-control" placeholder={$_('Filter results by zone idna, domain idna, domain ID')} bind:this={mainInput} bind:value={$text} disabled={updating} />
                        {#if $text}
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                    </div>
                </div>
                <div class="col-sm-3">
                    <div class="float-end">
                        <button class="btn btn-primary" type="submit" disabled={updating}>{$_('Filter')}</button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="card mt-4">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card mt-4">
        <div class="card-title text-primary">{$_('Notifications')}</div>

        {#if $results.length}
            <div class="card-body">
                <table class="table table-striped table-condensed mb-0" style="table-layout: fixed;" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th style="width: 175px;">{$_('Date')}</th>
                            <th style="width: 60px;">{$_('Mode')}</th>
                            <th style="width: 240px;">{$_('Destination')}</th>
                            <th>{$_('Domains')}</th>
                            <th>{$_('Zones')}</th>
                            <th style="width: 80px;" class="tt-left" data-tooltip={$_('Ce domaine/zone faisait parti\nd’une autre notification dans la même batch\n(par exemple, nb de jours différents)')}>{$_('Skipped?')}</th>
                            <th style="width: 80px;">{$_('Client')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.id)}
                            <tr>
                                <td><Date value={item.stamp} format="h-table-s" /></td>
                                <td>{item.delivery_mode}</td>
                                <td>
                                    {#if item.delivery_mode == 'sms'}<InternationalPhone phone={item.delivery_args.phone} />
                                    {:else}{item.delivery_args.email}
                                    {/if}
                                </td>
                                <td>
                                    {#each item.domain_idnas as idna}
                                        {idna}
                                    {/each}
                                </td>
                                <td>
                                    {#each item.zone_idnas as idna}
                                        {idna}
                                    {/each}
                                </td>
                                <td>
                                    {#if item.skipped_on_duplicate}<span>{$_('sautée')}</span>{/if}
                                </td>
                                <td><a use:sanchor href={spath(`/wdom-user/${item.user_id}`)}><Icon inline icon="fa:user" /></a></td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer text-bg-light">
            <PageList
                loading={updating}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
    </div>
{/if}
